/**
 * Utility functions for building AI context from patient data
 * Ensures no PII (Personally Identifiable Information) is included
 */

import { Patient } from '../types/patient'
import { Consultation } from '../types/consultation'
import { ConsultationContext } from '../services/aiService'

/**
 * Calculate age from date of birth without exposing the actual date
 */
function calculateAge(dateOfBirth: string): number | undefined {
  if (!dateOfBirth) return undefined
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

/**
 * Build a sanitized ConsultationContext from patient and consultation data
 * Excludes all PII: names, addresses, phone numbers, emails, IDs, insurance details, etc.
 * Only includes medical information needed for AI suggestions
 */
export function buildSanitizedConsultationContext(
  patient: Patient,
  consultations?: Consultation[]
): ConsultationContext {
  // Calculate age (not PII, just a number)
  const patientAge = calculateAge(patient.dateOfBirth)

  // Aggregate diagnosis from recent consultations
  const diagnosisSet = new Set<string>()
  if (consultations) {
    consultations.forEach((consultation) => {
      if (consultation.diagnosis) {
        consultation.diagnosis.forEach((d) => diagnosisSet.add(d))
      }
    })
  }
  const aggregatedDiagnosis = Array.from(diagnosisSet)

  // Aggregate notes from recent consultations (last 5 most recent)
  // NOTE: Consultation notes may contain PII if entered by users. This is a data quality issue
  // that should be addressed through user training and data entry guidelines.
  const recentNotes = consultations
    ?.slice()
    .sort((a, b) => new Date(b.consultationDate).getTime() - new Date(a.consultationDate).getTime())
    .slice(0, 5)
    .map((c) => c.notes)
    .filter((notes): notes is string => !!notes)
    .join('\n\n---\n\n') || ''

  // Build context - ONLY medical information, NO PII
  return {
    // Demographics (anonymized - only age and gender, no names or DOB)
    patientAge,
    patientGender: patient.gender, // Gender is not considered PII

    // Medical information only
    currentMedications: patient.currentMedications,
    allergies: patient.allergies,
    chronicConditions: patient.chronicConditions,
    medicalHistory: patient.medicalHistory, // Medical history text (should not contain PII)

    // Consultation data
    diagnosis: aggregatedDiagnosis.length > 0 ? aggregatedDiagnosis : undefined,
    notes: recentNotes || undefined,
  }
}

/**
 * Build a ConsultationContext with specific consultation notes
 * Used for consultation workspace context
 */
export function buildConsultationContextWithNotes(
  patient: Patient,
  reasonForVisit?: string,
  notes?: string,
  diagnosis?: string[]
): ConsultationContext {
  const patientAge = calculateAge(patient.dateOfBirth)

  return {
    patientAge,
    patientGender: patient.gender,
    reasonForVisit,
    notes,
    currentMedications: patient.currentMedications,
    allergies: patient.allergies,
    chronicConditions: patient.chronicConditions,
    medicalHistory: patient.medicalHistory,
    diagnosis,
  }
}






























